/**
 * Dashboard-specific top bar used by the editor shell.
 */
Vue.asyncComponent('cms-dashboard-editor-top-bar', {
    /**
     * Create the reactive state for this dashboard component.
     */
    data() {
        return {
            featureHandler: null,
            topBarWidgets: null,
            showDashboardTabSortModal: false,
            showDashboardTabRenameModal: false,
            dashboardTabToRename: null,
            dashboardTabRenameTitle: '',
            sortableDashboardTabs: [],
            draggedSortableDashboardTabId: null,
            sortableDashboardTabDropTarget: null,
        };
    },
    props: {
        blueprintId: {
            type: String,
            required: true,
        },
        verticalId: {
            type: String,
            required: true,
        },
        visibility: {
            type: Object,
        },
        widgetBaseUrl: {
            type: String,
        },
        dataProvider: {
            type: Object,
            default: false,
        },
        dashboardTabs: {
            type: Array,
            default: () => [],
        },
        activeDashboardTabId: {
            type: String,
            default: null,
        },
    },
    computed: {
        /**
         * Keep the page settings affordances consistent with the old editor shell.
         */
        showPageSettings() {
            return !this.$slots.left && (
                this.showBlueprintCopy ||
                this.showBlueprintShareLink ||
                this.featureHandler.featureSupported('blueprint-header', true) ||
                this.featureHandler.featureSupported('blueprint-metadata', true)
            );
        },
        /**
         * Return whether the copy action is enabled.
         */
        showBlueprintCopy() {
            return this.featureHandler.featureSupported('blueprint-copy', true);
        },
        /**
         * Return whether the share-link action is enabled.
         */
        showBlueprintShareLink() {
            return this.featureHandler.featureSupported('blueprint-share-link', true);
        },
        /**
         * Resolve the current dashboard title.
         */
        blueprintTitle() {
            return this.dataProvider.getTitle();
        },
        /**
         * Resolve the breadcrumb label.
         */
        listLabel() {
            return this.dataProvider.getListLabel();
        },
        /**
         * Resolve the publish toggle label based on version availability.
         */
        publishedToggleLabel() {
            return this.dataProvider.getVersionsCount() > 1
                ? this.$t('Deze versie publiceren')
                : this.$t('Gepubliceerd');
        },
        /**
         * Resolve the currently active dashboard tab id.
         */
        resolvedActiveDashboardTabId() {
            if (this.activeDashboardTabId) {
                return this.activeDashboardTabId;
            }

            return this.dashboardTabs[0]?.id || null;
        },
    },
    watch: {
        /** Keep the active tab visible after it changes, including after creation. */
        resolvedActiveDashboardTabId() {
            this.$nextTick(this.scrollActiveDashboardTabIntoView);
        },
    },
    methods: {
        /** Open the separate dashboard-tab sorting dialog. */
        openDashboardTabSortModal() {
            this.sortableDashboardTabs = [...this.dashboardTabs];
            this.showDashboardTabSortModal = true;
        },
        /** Apply the order prepared in the sorting dialog. */
        applyDashboardTabSort() {
            this.$emit('sortDashboardTabs', this.sortableDashboardTabs);
            this.showDashboardTabSortModal = false;
        },
        /** Start dragging one tab in the sorting dialog. */
        startSortableDashboardTabDrag(event, tab) {
            this.draggedSortableDashboardTabId = tab.id;
            this.sortableDashboardTabDropTarget = null;

            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', tab.id);
                this.setSortableDashboardTabDragPreview(event.dataTransfer, tab);
            }
        },
        /**
         * Use a complete tab row as the native drag image instead of the small
         * handle that initiated the drag interaction.
         *
         * @param {DataTransfer} dataTransfer
         * @param {*} tab
         */
        setSortableDashboardTabDragPreview(dataTransfer, tab) {
            if (typeof document === 'undefined' || typeof dataTransfer.setDragImage !== 'function') {
                return;
            }

            const preview = document.createElement('div');
            preview.className = 'cms-dashboard-editor__tab-sort-drag-preview';
            const handle = document.createElement('i');
            handle.className = 'ak-icon cms-dashboard-editor__tab-sort-drag-preview-handle';
            handle.textContent = 'drag_indicator';
            const label = document.createElement('span');
            label.textContent = tab.title;

            preview.append(handle, label);
            document.body.appendChild(preview);

            dataTransfer.setDragImage(preview, 20, 20);

            window.setTimeout(() => preview.remove(), 0);
        },
        /**
         * Show whether a dragged tab will be inserted before or after this tab
         * in the sorting dialog.
         *
         * @param {DragEvent} event
         * @param {*} tab
         */
        setSortableDashboardTabDropTarget(event, tab) {
            if (!this.draggedSortableDashboardTabId || this.draggedSortableDashboardTabId === tab.id) {
                return;
            }

            const rect = event.currentTarget.getBoundingClientRect();
            const placement = event.clientX < rect.left + (rect.width / 2) ? 'before' : 'after';

            this.sortableDashboardTabDropTarget = {
                tabId: tab.id,
                placement,
            };

            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
        },
        /**
         * Move the dragged dashboard tab within the sorting dialog.
         *
         * @param {*} tab
         */
        dropSortableDashboardTab(tab) {
            const sourceIndex = this.sortableDashboardTabs.findIndex((item) => item.id === this.draggedSortableDashboardTabId);
            const targetIndex = this.sortableDashboardTabs.findIndex((item) => item.id === tab.id);
            const placement = this.sortableDashboardTabDropTarget?.tabId === tab.id
                ? this.sortableDashboardTabDropTarget.placement
                : 'before';

            if (sourceIndex >= 0 && targetIndex >= 0 && sourceIndex !== targetIndex) {
                const tabs = [...this.sortableDashboardTabs];
                const [sourceTab] = tabs.splice(sourceIndex, 1);
                let insertionIndex = targetIndex + (placement === 'after' ? 1 : 0);

                if (sourceIndex < insertionIndex) {
                    insertionIndex -= 1;
                }

                tabs.splice(insertionIndex, 0, sourceTab);
                this.sortableDashboardTabs = tabs;
            }

            this.stopSortableDashboardTabDrag();
        },
        /** Clear the current sorting-dialog drag state. */
        stopSortableDashboardTabDrag() {
            this.draggedSortableDashboardTabId = null;
            this.sortableDashboardTabDropTarget = null;
        },
        /**
         * Open the metadata/settings route used by top bar widgets.
         */
        showSettings() {
            this.$router.push({
                name: 'blueprint-settings',
                query: this.$route.query,
                params: { activeTab: 'metadata' },
            });
        },
        /**
         * Navigate back to the dashboard list target.
         */
        async goToList() {
            const listUrl = await this.dataProvider.getListUrl();

            this.$actionHandler.handleRoute({ route: listUrl, target: this.target });
        },
        /**
         * Forward edit actions emitted by top bar widgets.
         */
        edit($event) {
            this.$router.push({
                name: 'blueprint-form',
                params: {
                    blueprintId: this.$route.params.blueprintId,
                    bundle: $event.bundleId,
                    definition: $event.definitionId,
                    objectId: $event.objectId,
                },
                query: this.$route.query,
            });
        },
        /**
         * Forward version changes to the parent editor shell.
         */
        changeVersion($event) {
            this.$emit('changeVersion', $event);
        },
        /**
         * Set one dashboard tab as active.
         *
         * @param {string} tabId
         */
        setActiveDashboardTab(tabId) {
            this.$emit('setActiveDashboardTab', tabId);
        },
        /** Open the tab-rename dialog with the current title prefilled. */
        openDashboardTabRenameModal(tab) {
            this.dashboardTabToRename = tab;
            this.dashboardTabRenameTitle = tab.title || '';
            this.showDashboardTabRenameModal = true;

            this.$nextTick(() => {
                this.$refs.dashboardTabRenameTitle?.focus();
                this.$refs.dashboardTabRenameTitle?.select();
            });
        },
        /** Submit a valid dashboard tab title to the parent editor. */
        submitDashboardTabRename() {
            const title = this.dashboardTabRenameTitle.trim();

            if (!title || !this.dashboardTabToRename) {
                return;
            }

            this.$emit('renameDashboardTab', {
                tab: this.dashboardTabToRename,
                title,
            });
            this.closeDashboardTabRenameModal();
        },
        /** Reset the tab-rename dialog. */
        closeDashboardTabRenameModal() {
            this.showDashboardTabRenameModal = false;
            this.dashboardTabToRename = null;
            this.dashboardTabRenameTitle = '';
        },
        /**
         * Trigger a delete request for one dashboard tab.
         *
         * @param {*} tab
         */
        deleteDashboardTab(tab) {
            this.$emit('deleteDashboardTab', tab);
        },
        /**
         * Trigger an add-tab request.
         */
        addDashboardTab() {
            this.$emit('addDashboardTab');
        },
        /** Scroll the active tab into the visible area of the tab navigation. */
        scrollActiveDashboardTabIntoView() {
            const tabs = this.$refs.dashboardTabsNavigation;
            const navigation = tabs?.$refs?.tabsNavigation;

            if (!navigation) {
                return;
            }

            tabs.checkTabsOverflow();

            const activeTab = navigation.querySelector('.cms-dashboard-editor__tab--active')
                ?.closest('.cms-dashboard-editor__tab-item');

            if (!activeTab) {
                return;
            }

            const activeTabStart = activeTab.offsetLeft;
            const activeTabEnd = activeTabStart + activeTab.offsetWidth;
            const visibleStart = navigation.scrollLeft;
            const visibleEnd = visibleStart + navigation.clientWidth;

            if (activeTabStart < visibleStart) {
                navigation.scrollTo({ left: activeTabStart, behavior: 'smooth' });
            } else if (activeTabEnd > visibleEnd) {
                navigation.scrollTo({ left: activeTabEnd - navigation.clientWidth, behavior: 'smooth' });
            }
        },
        /** Resolve one tab's popover component from the repeated tab list. */
        getDashboardTabPopover(tabId) {
            const reference = this.$refs[`dashboardTabPopover-${tabId}`];

            return Array.isArray(reference) ? reference[0] : reference;
        },
        /**
         * Move an open tab popover to the viewport so its menu is not clipped
         * by the horizontally scrollable tab strip.
         */
        positionDashboardTabPopover(tabId) {
            this.$nextTick(() => {
                const popover = this.getDashboardTabPopover(tabId);
                const menu = popover?.$refs?.menu;

                if (!popover || !menu) {
                    return;
                }

                const triggerRect = popover.$el.getBoundingClientRect();
                const menuWidth = menu.getBoundingClientRect().width;
                const top = triggerRect.bottom + 10;

                menu.style.position = 'fixed';
                menu.style.zIndex = '30';
                // The component normally uses min-width: 100% of its relative
                // wrapper. With a fixed position that percentage becomes the
                // viewport width, so retain the measured menu width instead.
                menu.style.width = `${menuWidth}px`;
                menu.style.minWidth = '0';
                menu.style.top = `${top}px`;
                menu.style.bottom = 'auto';

                if (triggerRect.right - menuWidth < 8) {
                    menu.style.left = `${Math.max(triggerRect.left, 8)}px`;
                    menu.style.right = 'auto';
                    return;
                }

                menu.style.left = 'auto';
                menu.style.right = `${Math.max(window.innerWidth - triggerRect.right, 8)}px`;
            });
        },
        /** Reset the viewport positioning when a tab popover closes. */
        resetDashboardTabPopover(tabId) {
            const popover = this.getDashboardTabPopover(tabId);
            const menu = popover?.$refs?.menu;

            if (!menu) {
                return;
            }

            ['position', 'z-index', 'width', 'min-width', 'top', 'bottom', 'left', 'right'].forEach((property) => {
                menu.style.removeProperty(property);
            });
        },
        /**
         * Refresh the editor shell and top bar widgets.
         */
        async refresh() {
            this.$emit('refresh');
            this.topBarWidgets = await this.dataProvider.getTopBarWidgets(true);
            this.featureHandler = this.dataProvider.getFeatureHandler();
        },
    },
    /**
     * Clean up dashboard behavior before the component is destroyed.
     */
    beforeDestroy() {
        this.$root.$el.classList.remove('has-ak-top-bar');
    },
    /**
     * Attach dashboard behavior after the component is mounted.
     */
    mounted() {
        this.$root.$el.classList.add('has-ak-top-bar');
    },
    /**
     * Initialize this dashboard component after it is created.
     */
    async created() {
        this.topBarWidgets = await this.dataProvider.getTopBarWidgets();
        this.featureHandler = this.dataProvider.getFeatureHandler();
    },
}, 'cms/dashboard/editor/cms-dashboard-editor-top-bar.html');
